import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CustomerContact, {
  CreditCardInfo,
  CustomerContactRole,
} from "../../models/CustomerContact";
import {
  createCustomerContact,
  getCustomerContactCreditCard,
  updateCustomerContact,
} from "../../api/ProcurementClient";
import { AxiosError } from "axios";
import Customer from "../../models/Customer";
import { Role } from "../../models/User";
import { AppContext } from "../../AppContextProvider";
import CustomerAutocomplete from "../CustomerAutocomplete";

const EMPTY_CARD: CreditCardInfo = {
  number: "",
  expiration: "",
  cvv: "",
  zip: "",
  nameOnCard: "",
};

const formatCardNumber = (value: string) =>
  value
    .replace(/\D/g, "")
    .slice(0, 19)
    .replace(/(\d{4})(?=\d)/g, "$1 ");

const formatExpiration = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 4);
  return digits.length > 2 ? `${digits.slice(0, 2)}/${digits.slice(2)}` : digits;
};

/** Mirrors backend validation; returns per-field errors for a partially filled card. */
const validateCard = (card: CreditCardInfo) => {
  const isEmpty = Object.values(card).every((v) => !v.trim());
  if (isEmpty) return { isEmpty, errors: {} as Partial<Record<keyof CreditCardInfo, string>> };
  const errors: Partial<Record<keyof CreditCardInfo, string>> = {};
  if (!/^\d{12,19}$/.test(card.number.replace(/\s/g, "")))
    errors.number = "12-19 digits";
  if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(card.expiration))
    errors.expiration = "MM/YY";
  if (!/^\d{3,4}$/.test(card.cvv)) errors.cvv = "3 or 4 digits";
  if (!card.zip.trim()) errors.zip = "Required";
  if (!card.nameOnCard.trim()) errors.nameOnCard = "Required";
  return { isEmpty, errors };
};

interface DialogProps {
  customer?: Customer;
  /** Pre-selected customer when creating a new contact. */
  defaultCustomer?: Customer;
  contactRole?: CustomerContactRole;
  contact?: CustomerContact;
  customerId?: number; // Required when creating new contacts with saveOnClose=true
  onClose: (contact?: CustomerContact) => void;
  saveOnClose: boolean;
}

const getRoleName = (role: CustomerContactRole): string => {
  switch (role) {
    case CustomerContactRole.BUYER:
      return "Buyer";
    case CustomerContactRole.RECEIVING:
      return "Receiving";
  }
};

const CustomerContactDialog: React.FC<DialogProps> = ({
  customer,
  defaultCustomer,
  contactRole,
  contact,
  onClose,
  saveOnClose,
}) => {
  const [role, setRole] = useState<CustomerContactRole>(
    contact?.role || contactRole || CustomerContactRole.BUYER
  );
  const { customers, user } = useContext(AppContext);
  const [pickerCustomer, setPickerCustomer] = useState<Customer | undefined>(
    () =>
      customer ??
      defaultCustomer ??
      (contact
        ? customers.find((c) => c.id === contact.customerId)
        : undefined)
  );

  const [name, setName] = useState(contact?.name ?? "");
  const [email, setEmail] = useState(contact?.email ?? "");
  const [phone, setPhone] = useState(contact?.phone ?? "");

  const showCreditCard =
    saveOnClose &&
    role === CustomerContactRole.BUYER &&
    (user.role === Role.ADMIN || user.role === Role.EMPLOYEE);
  const [card, setCard] = useState<CreditCardInfo>(EMPTY_CARD);
  const [cardDirty, setCardDirty] = useState(false);
  const [cardLoadError, setCardLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { isEmpty: cardIsEmpty, errors: cardErrors } = validateCard(card);
  const cardInvalid = !cardIsEmpty && Object.keys(cardErrors).length > 0;

  useEffect(() => {
    if (!showCreditCard || !contact?.id || !contact.creditCardLast4) return;
    let cancelled = false;
    getCustomerContactCreditCard(contact.id)
      .then((stored) => {
        if (cancelled || !stored) return;
        setCard({ ...stored, number: formatCardNumber(stored.number) });
      })
      .catch((e: Error) => {
        if (!cancelled) setCardLoadError(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, [showCreditCard, contact?.id, contact?.creditCardLast4]);

  const updateCard = (field: keyof CreditCardInfo, value: string) => {
    setCardDirty(true);
    setCard((prev) => ({ ...prev, [field]: value }));
  };

  const nameInputRef = useRef<HTMLInputElement>(null);

  const customerId = pickerCustomer?.id ?? customer?.id ?? 0;
  const dialogTitle = contact
    ? "Edit " + getRoleName(contact.role)
    : "New " + getRoleName(contactRole || CustomerContactRole.BUYER);

  useEffect(() => {
    // Focus on the name input when dialog opens
    // Use a small delay to ensure the dialog is fully rendered
    const timer = setTimeout(() => {
      if (nameInputRef.current) {
        nameInputRef.current.focus();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const isSaveDisabled =
    !name.trim() ||
    (!email.trim() && !phone.trim()) ||
    !customerId ||
    (showCreditCard && cardDirty && cardInvalid);

  const handleRoleChange = (event: SelectChangeEvent<CustomerContactRole>) => {
    setRole(event.target.value as CustomerContactRole);
  };

  const handleSave = async () => {
    const contactData = {
      role,
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim(),
      altPhone: "",
      altEmail: "",
      ...(showCreditCard && cardDirty
        ? {
            creditCard: cardIsEmpty
              ? null
              : {
                  ...card,
                  number: card.number.replace(/\s/g, ""),
                  zip: card.zip.trim(),
                  nameOnCard: card.nameOnCard.trim(),
                },
          }
        : {}),
    };

    if (saveOnClose) {
      setSaveError(null);
      try {
        let savedContact: CustomerContact;

        if (contact) {
          if (!customerId) {
            console.error("customerId is required when updating contacts");
            return;
          }
          savedContact = await updateCustomerContact(contact.id, {
            ...contactData,
            customerId,
          });
        } else {
          // Create new contact
          if (!customerId) {
            console.error(
              "customerId is required when creating new contacts with saveOnClose=true"
            );
            return;
          }
          savedContact = await createCustomerContact(customerId, contactData);
        }

        onClose(savedContact);
      } catch (error) {
        const axiosError = error as AxiosError;
        const message =
          axiosError.response?.data?.toString() ??
          axiosError.response?.statusText ??
          (error as Error)?.message ??
          "Unknown server error";
        console.error("Error saving contact: " + message);
        setSaveError(message);
      }
    } else {
      // Don't save to database, just pass the contact data
      const { creditCard: _creditCard, ...contactFields } = contactData;
      const tempContact: CustomerContact = {
        ...contactFields,
        id: contact?.id ?? 0,
        customerId: customerId ?? 0,
      };
      onClose(tempContact);
    }
  };

  const handleCancel = () => {
    onClose(); // No contact passed
  };

  return (
    <Dialog
      open={true}
      onClose={handleCancel}
      fullWidth={true}
      maxWidth="sm"
      disableRestoreFocus={true}
      aria-labelledby="customer-contact-dialog-title"
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <CustomerAutocomplete
            customer={pickerCustomer}
            required
            label="Client"
            onCustomerSelected={(selected) => setPickerCustomer(selected)}
          />

          {!contactRole && (
            <FormControl fullWidth variant="standard">
              <InputLabel id="role-label">Role</InputLabel>
              <Select
                labelId="role-label"
                value={role}
                onChange={handleRoleChange}
              >
                {Object.values(CustomerContactRole).map((roleValue) => (
                  <MenuItem key={roleValue} value={roleValue}>
                    {roleValue.charAt(0).toUpperCase() + roleValue.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth
            required={true}
            label="Name"
            variant="standard"
            value={name}
            inputRef={nameInputRef}
            autoFocus
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setName(event.target.value)
            }
          />

          <TextField
            fullWidth
            label="Phone"
            variant="standard"
            value={phone}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setPhone(event.target.value)
            }
          />
          <TextField
            fullWidth
            label="Email"
            variant="standard"
            type="email"
            value={email}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(event.target.value)
            }
          />

          {showCreditCard && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Credit Card
              </Typography>
              {cardLoadError && (
                <Typography variant="caption" color="error">
                  {cardLoadError}
                </Typography>
              )}
              <TextField
                fullWidth
                label="Credit Card #"
                variant="standard"
                value={card.number}
                autoComplete="off"
                inputProps={{ inputMode: "numeric" }}
                error={cardDirty && !!cardErrors.number}
                helperText={cardDirty ? cardErrors.number : undefined}
                onChange={(e) =>
                  updateCard("number", formatCardNumber(e.target.value))
                }
              />
              <Stack direction="row" gap={2}>
                <TextField
                  label="Expiration (MM/YY)"
                  variant="standard"
                  value={card.expiration}
                  autoComplete="off"
                  inputProps={{ inputMode: "numeric" }}
                  error={cardDirty && !!cardErrors.expiration}
                  helperText={cardDirty ? cardErrors.expiration : undefined}
                  onChange={(e) =>
                    updateCard("expiration", formatExpiration(e.target.value))
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="CVV"
                  variant="standard"
                  value={card.cvv}
                  autoComplete="off"
                  inputProps={{ inputMode: "numeric" }}
                  error={cardDirty && !!cardErrors.cvv}
                  helperText={cardDirty ? cardErrors.cvv : undefined}
                  onChange={(e) =>
                    updateCard("cvv", e.target.value.replace(/\D/g, "").slice(0, 4))
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Zip Code"
                  variant="standard"
                  value={card.zip}
                  autoComplete="off"
                  error={cardDirty && !!cardErrors.zip}
                  helperText={cardDirty ? cardErrors.zip : undefined}
                  onChange={(e) => updateCard("zip", e.target.value.slice(0, 10))}
                  sx={{ flex: 1 }}
                />
              </Stack>
              <TextField
                fullWidth
                label="Name on Card"
                variant="standard"
                value={card.nameOnCard}
                autoComplete="off"
                error={cardDirty && !!cardErrors.nameOnCard}
                helperText={cardDirty ? cardErrors.nameOnCard : undefined}
                onChange={(e) => updateCard("nameOnCard", e.target.value)}
              />
            </>
          )}
          {saveError && (
            <Typography variant="body2" color="error">
              {saveError}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaveDisabled}
        >
          {saveOnClose ? "Save" : "OK"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerContactDialog;
